<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import useLoading from '../../../../../Composables/useLoading.ts'
import useModal from '../../../../../Composables/useModal.ts'
import { RepurchasedContractType } from '../../../../../Constants/RepurchasedContractType.ts'
import type { FormCreatorType } from '../../../../../Types/form'
import type { AnyRepurchasedContract } from '../../../../../Types/restructuring'
import ButtonAll from '../../UI/Button/ButtonAll.vue'
import FieldCreator from '../../UI/Form/FieldCreator.vue'
import FormCreator from '../../UI/Form/FormCreator.vue'
import LazyContent from '../../UI/Loading/LazyContent.vue'
import ModalDialog from '../../UI/Modal/ModalDialog.vue'

type FormDefParameters = {
  type?: RepurchasedContractType
  originalContract?: number
  repurchaseDate?: string
}

const props = defineProps<{
  restructuringId: number
  actionId: number
  contractId?: number | null
  contractType: RepurchasedContractType
}>()

const emit = defineEmits<{
  close: []
  save: [{ contract: AnyRepurchasedContract }]
}>()

const { loading, setLoading } = useLoading(true)
const { loading: sending } = useLoading(false)
const { t } = useI18n()

const endpoint = computed<string>(
  () => `contracts/restructuring/${props.restructuringId}/outstanding-repurchase/${props.actionId}/contracts`,
)

const contract = ref<AnyRepurchasedContract | null>(null)

const { modal, modalId, open, close } = useModal({ prefix: 'external-contract-' })
const modalTitle = computed<string>(() => {
  if (props.contractType === RepurchasedContractType.external) {
    return t(
      props.contractId ? 'restructuring.actions.editExternalContract' : 'restructuring.actions.addExternalContract',
    )
  }

  return t(props.contractId ? 'restructuring.actions.editContract' : 'restructuring.actions.addContract')
})

const changedContract = ref<boolean>(false)
const shouldSendRepurchaseDate = computed(() => !!props.contractId || !changedContract.value)

const defParams = computed(() => {
  const params: FormDefParameters = {}
  if (!props.contractId) {
    params.type = props.contractType
  }

  if (props.contractType !== RepurchasedContractType.default) {
    return params
  }

  //@ts-ignore Property 'originalContract' does not exist on type '{}' ➡️ Requires generics on FormCreator
  const originalContractId = form.value?.datas?.originalContract
  if (!props.contractId && !!originalContractId) {
    params.originalContract = originalContractId
  }

  //@ts-ignore Property 'repurchaseDate' does not exist on type '{}' ➡️ Requires generics on FormCreator
  const repurchaseDate = form.value?.datas?.repurchaseDate
  if (!!repurchaseDate && shouldSendRepurchaseDate.value) {
    params.repurchaseDate = repurchaseDate
  }

  return params
})

const form = ref<FormCreatorType | null>(null)

async function submit() {
  sending.value = true
  try {
    await form.value?.methods()
  } finally {
    sending.value = false
  }
}

function setContract(value: AnyRepurchasedContract) {
  contract.value = value
}

function onContractChanged() {
  changedContract.value = true
  refreshFormDef()
}

function onFormLoad(formCreator: FormCreatorType) {
  form.value = formCreator
}

function submitted(contract: AnyRepurchasedContract) {
  emit('save', { contract })
  close()
}

async function refreshFormDef() {
  await nextTick()
  await form.value?.methods('get')
  changedContract.value = false
}

onMounted(() => {
  open()
})
</script>

<template>
  <ModalDialog
    ref="modal"
    :id="modalId"
    :title="modalTitle"
    class-body="px-3"
    :close-with-backdrop="false"
    @icon-close="emit('close')"
  >
    <template #body>
      <FormCreator
        :endpoint="endpoint"
        :params="defParams"
        :id="contractId"
        :use-id-in-def="!!contractId"
        src-error="formrepurchasedcontract"
        class-to-add="d-none"
        @object-build="setContract"
        @form-return="submitted"
        @update-data="onFormLoad"
        @is-loading="setLoading"
      />

      <LazyContent :loading="loading" :class="{ 'p-2': loading }">
        <div class="row" v-if="form">
          <FieldCreator name="type" form-ref="formrepurchasedcontract" :form-data="form" />

          <div class="col-12">
            <FieldCreator
              name="originalContract"
              form-ref="formrepurchasedcontract"
              :form-data="form"
              label="restructuring.fields.outstandingRepurchase.contract.originalContract.label"
              group-property="client.name"
              label-property="text"
              value-property="id"
              label-top
              @get-select-change="onContractChanged"
            />
          </div>
          <div class="col-12">
            <FieldCreator
              name="number"
              form-ref="formrepurchasedcontract"
              :form-data="form"
              label="restructuring.fields.outstandingRepurchase.contract.number.label"
              label-top
            />
          </div>
          <div class="col-12">
            <FieldCreator
              name="client"
              form-ref="formrepurchasedcontract"
              :form-data="form"
              label="restructuring.fields.outstandingRepurchase.contract.client.label"
              label-top
            />
          </div>
          <div class="col-12">
            <FieldCreator
              name="repurchaseDate"
              form-ref="formrepurchasedcontract"
              :form-data="form"
              label="restructuring.fields.outstandingRepurchase.contract.repurchaseDate.label"
              type-value="string"
              label-top
              @get-select-change="refreshFormDef"
            />
          </div>
          <div class="col-12">
            <FieldCreator
              name="repurchaseAmount"
              form-ref="formrepurchasedcontract"
              :form-data="form"
              label="restructuring.fields.outstandingRepurchase.contract.repurchaseAmount.label"
              label-top
            />
          </div>
          <div class="col-12">
            <FieldCreator
              name="refinancer"
              form-ref="formrepurchasedcontract"
              :form-data="form"
              label="restructuring.fields.outstandingRepurchase.contract.refinancer.label"
              placeholder="restructuring.fields.outstandingRepurchase.contract.refinancer.placeholder"
              label-top
              allow-null
              not-remove
            />
          </div>
          <div class="col-12">
            <FieldCreator
              name="agreementNumber"
              form-ref="formrepurchasedcontract"
              :form-data="form"
              label="restructuring.fields.outstandingRepurchase.contract.agreementNumber.label"
              label-top
            />
          </div>
        </div>
      </LazyContent>
    </template>

    <template #footer v-if="!loading">
      <div class="d-flex w-100 justify-content-end">
        <ButtonAll margin="no" type="default" label="button.cancel" @click="close" />
        <ButtonAll margin="no" type="action" label="button.confirm" class="ml-2" :loading="sending" @click="submit" />
      </div>
    </template>
  </ModalDialog>
</template>
